<template>
  <div class="tabs-offer">
    <!-- Horizontal Menu -->
    <div
      v-if="horizontalMenu"
      class="horizontal-menu-wrapper"
    >
      <div class="horizontal-menu">
        <button
          v-for="item in horizontalMenuWithOrders"
          :key="item.id"
          class="menu-item"
          :class="{ 'selected-menu-item': selectedPrimaryNodeComputed?.id === item.id }"
          @click="setSelectedPrimaryNode(item)"
        >
          <div class="menu-item-content">
            <span class="horizontal-menu-text">{{ item.name }}</span>
            <span
              v-if="item.listings.length > 0"
              class="horizontal-menu-bubble"
            >
              {{ item.listings.length }}
            </span>
          </div>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="content-wrapper">
      <div
        v-if="pending"
        class="loading-indicator"
      >
        <i class="fas fa-spinner fa-spin" />
      </div>

      <div class="content-list">
        <div
          v-for="entry in contentList"
          :key="entry.id"
          class="content-section"
        >
          <div
            v-if="entry.name"
            class="sub-menu-title"
          >
            {{ entry.name }}
          </div>

          <div class="listings-container">
            <div
              v-for="listing in entry.listings"
              :key="listing.id"
              class="listing-item"
            >
              <ListingRow
                :listing="listing"
                :details="details"
                :catalog="catalog"
                :payer-id="payer.payerId"
                :day="day"
                :status="getStatus(listing)"
                :products="catalog?.products"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ListingRow from './ListingRow';

export default {
  name: 'TabsOffer',
  props: {
    payer: {
      type: Object,
      required: true,
    },
    orders: {
      type: Object,
      default: null,
    },
    catalog: {
      type: Object,
      default: null,
    },
    pending: {
      type: Boolean,
      default: false,
    },
    details: {
      type: Boolean,
      default: false,
    },
    batch: {
      type: Boolean,
      default: false,
    },
    day: {
      type: String,
      default: null,
    },
    calendar: {
      type: Array,
      default: () => [],
    },
    onRefresh: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      selectedPrimaryNode: null,
    };
  },
  components: {
    ListingRow,
  },
  computed: {
    ...mapGetters(['catalogBasket']),

    basket() {
      return this.catalogBasket || [];
    },

    horizontalMenu() {
      return this.catalog?.layout?.['1']?.nodes || null;
    },

    selectedPrimaryNodeComputed() {
      return this.selectedPrimaryNode;
    },

    horizontalMenuWithOrders() {
      return this.horizontalMenu?.map((m) => ({
        ...m,
        listings: this.listings
          .filter((x) => x.nodeIds.some((id) => m?.id === id))
          .filter((x) => x.payerIds.some((id) => this.payer.payerId === id))
          .filter((x) => this.getStatus(x) === 'inBasket'),
      }));
    },

    listings() {
      return (this.catalog?.listings || [])
        .map((x) => {
          const calendarEntry = this.calendar?.find((c) => c.listingId === x.id);

          if (!calendarEntry) {
            return null;
          }

          return {
            ...x,
            canOrder: calendarEntry.canOrder,
            orderFrom: calendarEntry.orderFrom,
            orderTo: calendarEntry.orderTo,
            canCancel: calendarEntry.canCancel,
            cancelTo: calendarEntry.cancelTo,
            menu: calendarEntry.menu,
          };
        })
        .filter((x) => x !== null);
    },

    filteredListings() {
      if (this.horizontalMenu) {
        return this.listings
          .filter((x) => x.nodeIds.some((id) => this.selectedPrimaryNodeComputed?.id === id))
          .filter((x) => x.payerIds.some((id) => this.payer.payerId === id));
      }

      return this.listings.filter((x) =>
        x.payerIds.some((id) => this.payer.payerId === id));
    },

    contentList() {
      const secondaryNodes = (this.catalog?.layout?.['2']?.nodes || [])
        .map((n) => ({
          ...n,
          listings: this.filteredListings.filter((x) => x.nodeIds.includes(n.id)),
        }));

      const otherListings = this.filteredListings.filter(
        (x) => !this.catalog?.layout?.['2']
             || !this.catalog.layout['2'].nodes.some((n) => x.nodeIds.includes(n.id)),
      );

      if (otherListings.length > 0) {
        secondaryNodes.push({
          id: '',
          name: this.catalog?.layout?.['2'] ? 'Inne' : '',
          listings: otherListings,
        });
      }

      return secondaryNodes;
    },
  },
  watch: {
    catalog: {
      handler(newCatalog) {
        if (newCatalog?.layout?.['1']?.nodes?.length > 0 && !this.selectedPrimaryNode) {
          [this.selectedPrimaryNode] = newCatalog.layout['1'].nodes;
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['showListingDetails', 'removeOrderBasketEntry2']),

    setSelectedPrimaryNode(node) {
      this.selectedPrimaryNode = node;
    },

    getComment(item) {
      const basketEntry = this.basket.find(
        (x) => x.payerId === this.payer.payerId && x.offerId === item.id,
      );

      const order = this.orders?.orders.find(
        (x) => x.availabilityId === item.id && x.payerId === this.payer.payerId,
      );

      return basketEntry?.comment || order?.comment;
    },

    getStatus(item) {
      if (this.batch) {
        const listingsInBasket = this.basket.filter(
          (x) => x.payerId === this.payer.payerId && x.offerId === item.id,
        );

        const daysOrdered = this.orders?.orders
          .filter((x) => x.payerId === this.payer.payerId)
          .map((x) => x.day);

        const allInBasket = (this.calendar || [])
          .filter((x) => x.canOrder && !daysOrdered?.includes(x.day))
          .map((x) => x.day)
          .every((d) => listingsInBasket.some((l) => l.day === d));

        return allInBasket ? 'inBasket' : 'available';
      }

      const basketEntry = this.basket.find(
        (x) => x.payerId === this.payer.payerId
             && x.offerId === item.id
             && (!this.day || x.day === this.day),
      );

      const order = this.orders?.orders.find(
        (x) => x.availabilityId === item.id
             && x.payerId === this.payer.payerId
             && (!this.day || x.day === this.day),
      );

      if (order) {
        if (basketEntry && basketEntry.isReturned) {
          return order.isPaid ? 'paidCancelled' : 'unpaidCancelled';
        }
        return order.isPaid ? 'paid' : 'unpaid';
      }

      return basketEntry ? 'inBasket' : 'available';
    },

    toBasketEntry(item) {
      return {
        payerId: this.payer.payerId,
        payeeId: this.payer.payeeId,
        catalogId: this.catalog?.catalog.id,
        offerName: item.name,
        offerId: item.id,
        isReturned: false,
        comment: this.getComment(item),
        isPaid: this.getStatus(item) === 'paid',
        currency: item.currency,
        price: item.price,
        day: this.day,
      };
    },

    // getConflicts(item, lday) {
    //   return this.basket
    //     .filter((x) => x.day === lday)
    //     .filter((x) => {
    //       const listing = this.catalog?.listings.find((l) => l.id === x.offerId);
    //       if (!listing) {
    //         return false;
    //       }
    //       return listing.productIds.some((p) => item.productIds.includes(p));
    //     });
    // },

    // toggleWithoutConflicts(item, lday, status) {
    //   if (this.batch && this.orders?.orders.some((x) => x.day === lday)) {
    //     // in batch mode, don't touch days that have something ordered
    //     return;
    //   }

    //   const conflicts = this.getConflicts(item, lday);

    //   // remove conflicts from basket
    //   conflicts.forEach((conflict) => {
    //     this.removeOrderBasketEntry2({
    //       ...conflict,
    //       isReturned: true,
    //     });
    //   });

    //   this.toggle(status, { ...this.toBasketEntry(item), day: lday });
    // },

    // handleListingClick(listing) {
    //   if (this.catalog?.catalog.orderSchema === 'Single') {
    //     this.showListingDetails({
    //       ...this.toBasketEntry(listing),
    //       status: this.getStatus(listing),
    //       listing,
    //       catalog: this.catalog,
    //     });
    //     this.$emit('show-offer-modal');
    //   } else {
    //     const status = this.getStatus(listing);
    //     const ordered = ['paid', 'unpaid', 'paidCancelled', 'unpaidCancelled'].includes(status);

    //     if (ordered && listing.canCancel) {
    //       this.toggle(status, { ...this.toBasketEntry(listing), day: this.day });
    //     } else if (!ordered && listing.canOrder) {
    //       if (this.batch) {
    //         this.calendar
    //           ?.filter((x) => x.listingId === listing.id)
    //           .forEach((d) => {
    //             this.toggleWithoutConflicts(listing, d.day, status);
    //           });
    //       } else {
    //         this.toggleWithoutConflicts(listing, this.day, status);
    //       }
    //     }
    //   }
    // },
  },
};
</script>

<style lang="scss" scoped>
.tabs-offer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.horizontal-menu-wrapper {
  padding: 0 15px;
  background-color: #fafafa;
  padding-top: 7px;
  border-bottom: 1px solid #eee;
}

.horizontal-menu {
  display: flex;
  overflow-x: auto;
  height: 30px;
  flex-grow: 0;
}

.menu-item {
  padding: 5px 15px;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;

  &.selected-menu-item {
    border-bottom: 2px solid #007bff;
    padding-bottom: 6px;
  }
}

.menu-item-content {
  display: flex;
  align-items: center;
}

.horizontal-menu-text {
  font-size: 12px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
}

.horizontal-menu-bubble {
  background-color: #007bff;
  color: white;
  width: 15px;
  height: 15px;
  text-align: center;
  margin-left: 3px;
  font-size: 10px;
  font-weight: bold;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-wrapper {
  flex-grow: 1;
  overflow-y: auto;
  padding: 10px;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  padding: 20px;

  i {
    font-size: 24px;
    color: #007bff;
  }
}

.content-section {
  margin-bottom: 20px;
}

.sub-menu-title {
  font-size: 18px;
  font-family: 'Poppins', sans-serif;
  font-weight: bold;
  margin-top: 10px;
  margin-left: 7px;
  margin-bottom: 10px;
}

.listings-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.listing-item {
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}
</style>
